/*
 * lali-pdp-runtime.js — Replaces instant-core-local.min.js for the PDP.
 * Initializes Swiper sliders, accordions, ticker, and countdown for elements
 * with data-instant-type=* attributes that the original Instant runtime handled.
 *
 * Depends on: Swiper 11 (loaded as window.Swiper via swiper-bundle-instant.min.js).
 *
 * Does NOT touch:
 *   - product form / cart-add / ATC
 *   - variant picker (has its own capture-phase handler in pdp-above-fold)
 *   - GTM / Stape / Meta pixel events
 *   - Anything outside the PDP
 */

(function () {
  'use strict';

  // -----------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------
  function whenSwiperReady(fn) {
    if (typeof window.Swiper !== 'undefined') {
      fn();
      return;
    }
    var tries = 0;
    var iv = setInterval(function () {
      tries += 1;
      if (typeof window.Swiper !== 'undefined') {
        clearInterval(iv);
        fn();
      } else if (tries > 200) {
        // 10s timeout, give up
        clearInterval(iv);
        console.warn('[lali-pdp-runtime] Swiper did not load within 10s');
      }
    }, 50);
  }

  function parseConfig(scriptEl) {
    if (!scriptEl) return null;
    try {
      return JSON.parse(scriptEl.textContent || scriptEl.innerText || '');
    } catch (e) {
      console.error('[lali-pdp-runtime] Invalid slider config', e);
      return null;
    }
  }

  // Strip Instant-specific fields and pass everything else through to Swiper.
  function buildSwiperOpts(cfg, opts) {
    var out = Object.assign({}, cfg);
    // These match Swiper 11 options 1:1 — no transformation needed.
    // Only thing to fix: navigation may be `false` or an object
    if (out.navigation === false) delete out.navigation;
    if (opts && opts.thumbsSwiper) {
      out.thumbs = { swiper: opts.thumbsSwiper };
    }
    return out;
  }

  // -----------------------------------------------------------
  // Sliders (Swiper)
  // -----------------------------------------------------------
  function initSliders() {
    // Find all slider/thumbnails elements grouped by data-instant-slider-id
    // so we can wire main↔thumbs together.
    var elements = document.querySelectorAll(
      '[data-instant-type="slider"], [data-instant-type="thumbnails"]'
    );
    if (!elements.length) return;

    // Group by data-instant-slider-id (main and thumbs share the same id)
    var groups = {};
    Array.prototype.forEach.call(elements, function (el) {
      var gid = el.getAttribute('data-instant-slider-id');
      if (!gid) return;
      if (!groups[gid]) groups[gid] = {};
      var type = el.getAttribute('data-instant-type');
      groups[gid][type] = el;
    });

    Object.keys(groups).forEach(function (gid) {
      var g = groups[gid];
      var mainEl = g.slider;
      var thumbsEl = g.thumbnails;
      if (mainEl && mainEl.dataset.laliSliderInit) return;

      // Init thumbs first if present, then main with thumbs link
      var thumbsSwiper = null;
      if (thumbsEl && !thumbsEl.dataset.laliSliderInit) {
        var thumbsCfg = parseConfig(
          document.getElementById('instant-slider-' + thumbsEl.id + '-params')
        );
        if (thumbsCfg) {
          try {
            thumbsSwiper = new window.Swiper(thumbsEl, buildSwiperOpts(thumbsCfg));
            thumbsEl.dataset.laliSliderInit = '1';
          } catch (e) {
            console.error('[lali-pdp-runtime] thumbs init failed', gid, e);
          }
        }
      }

      if (mainEl) {
        var mainCfg = parseConfig(
          document.getElementById('instant-slider-' + mainEl.id + '-params')
        );
        if (mainCfg) {
          try {
            var opts = buildSwiperOpts(mainCfg, { thumbsSwiper: thumbsSwiper });
            // eslint-disable-next-line no-new
            new window.Swiper(mainEl, opts);
            mainEl.dataset.laliSliderInit = '1';
          } catch (e) {
            console.error('[lali-pdp-runtime] main slider init failed', gid, e);
          }
        }
      }
    });
  }

  // -----------------------------------------------------------
  // Accordions
  // -----------------------------------------------------------
  function initAccordions() {
    var items = document.querySelectorAll('[data-instant-type="accordion-item"]');
    Array.prototype.forEach.call(items, function (item) {
      if (item.dataset.laliAccordionInit) return;
      item.dataset.laliAccordionInit = '1';

      var header = item.querySelector('[data-instant-type="accordion-header"]');
      var content = item.querySelector('[data-instant-type="accordion-content"]');
      if (!header || !content) return;

      // Sync visual state with data-state on load
      applyAccordionState(item, content);

      header.addEventListener('click', function (e) {
        // Don't hijack clicks on links/buttons inside the header that aren't the header itself
        if (e.target !== header && e.target.closest('a, button[type="submit"]')) return;
        e.preventDefault();
        var container = item.closest('[data-instant-type="accordion-container"]');
        var allowMultiOpen = container && container.getAttribute('data-is-multi-open-enabled') === 'true';

        var nextState = item.getAttribute('data-state') === 'open' ? 'closed' : 'open';

        if (nextState === 'open' && container && !allowMultiOpen) {
          // Close all siblings
          container.querySelectorAll('[data-instant-type="accordion-item"]').forEach(function (sib) {
            if (sib !== item) {
              sib.setAttribute('data-state', 'closed');
              var sc = sib.querySelector('[data-instant-type="accordion-content"]');
              if (sc) applyAccordionState(sib, sc);
            }
          });
        }

        item.setAttribute('data-state', nextState);
        applyAccordionState(item, content);
      });
    });
  }

  function applyAccordionState(item, content) {
    var open = item.getAttribute('data-state') === 'open';
    if (open) {
      var h = content.scrollHeight;
      content.style.setProperty('--instant-accordion-content-height', h + 'px');
    } else {
      content.style.setProperty('--instant-accordion-content-height', '0px');
    }
  }

  // -----------------------------------------------------------
  // Ticker (CSS animation)
  // -----------------------------------------------------------
  function initTickers() {
    var tickers = document.querySelectorAll('[data-instant-type="ticker"]');
    if (!tickers.length) return;

    // Inject @keyframes once
    if (!document.getElementById('lali-pdp-ticker-keyframes')) {
      var styleEl = document.createElement('style');
      styleEl.id = 'lali-pdp-ticker-keyframes';
      styleEl.textContent =
        '@keyframes lali-pdp-ticker-scroll{from{transform:translate3d(0,0,0)}to{transform:translate3d(-50%,0,0)}}';
      document.head.appendChild(styleEl);
    }

    Array.prototype.forEach.call(tickers, function (t) {
      if (t.dataset.laliTickerInit) return;
      t.dataset.laliTickerInit = '1';

      var inner = t.querySelector('.instant-ticker');
      if (!inner) return;

      // Duplicate inner so the loop is seamless
      var clone = inner.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      t.appendChild(clone);

      // speed attribute: lower = faster animation? Instant uses 50 as a baseline.
      var speed = parseInt(t.getAttribute('data-instant-ticker-speed') || '50', 10);
      // Map speed to duration: 50 → 30s, 100 → 15s, etc.
      var duration = Math.max(8, Math.round(1500 / Math.max(speed, 1)));

      // Apply animation to BOTH copies (the track) — set on the parent .instant-ticker pair
      t.style.display = t.style.display || 'flex';
      t.style.overflow = 'hidden';
      inner.style.display = 'flex';
      inner.style.flex = '0 0 auto';
      clone.style.display = 'flex';
      clone.style.flex = '0 0 auto';

      // We animate a wrapper, so wrap both .instant-ticker in a flex track
      var track = document.createElement('div');
      track.className = 'lali-pdp-ticker-track';
      track.style.cssText =
        'display:flex;flex-wrap:nowrap;width:max-content;animation:lali-pdp-ticker-scroll ' +
        duration +
        's linear infinite;will-change:transform';
      t.insertBefore(track, inner);
      track.appendChild(inner);
      track.appendChild(clone);
    });
  }

  // -----------------------------------------------------------
  // Countdown
  // -----------------------------------------------------------
  function initCountdowns() {
    var els = document.querySelectorAll('[data-instant-type="countdown"]');
    if (!els.length) return;

    Array.prototype.forEach.call(els, function (cd) {
      if (cd.dataset.laliCountdownInit) return;
      cd.dataset.laliCountdownInit = '1';

      var dateStr = cd.getAttribute('data-instant-countdown-date');
      if (!dateStr) return;

      var target = new Date(dateStr).getTime();
      if (isNaN(target)) return;
      var leadingZeros = cd.getAttribute('data-instant-countdown-leading-zeros') === 'true';

      function pad(n) {
        n = Math.max(0, n | 0);
        if (!leadingZeros) return String(n);
        return n < 10 ? '0' + n : String(n);
      }

      function tick() {
        var diff = target - Date.now();
        if (diff < 0) diff = 0;
        var d = Math.floor(diff / 86400000);
        var h = Math.floor((diff % 86400000) / 3600000);
        var m = Math.floor((diff % 3600000) / 60000);
        var s = Math.floor((diff % 60000) / 1000);
        // Look up child elements for each unit (Instant convention)
        setUnit(cd, 'days', pad(d));
        setUnit(cd, 'hours', pad(h));
        setUnit(cd, 'minutes', pad(m));
        setUnit(cd, 'seconds', pad(s));
      }

      function setUnit(root, unit, value) {
        var el = root.querySelector('[data-instant-countdown-unit="' + unit + '"]');
        if (el) el.textContent = value;
      }

      tick();
      var iv = setInterval(function () {
        if (Date.now() > target) {
          clearInterval(iv);
          tick();
          return;
        }
        tick();
      }, 1000);
    });
  }

  // -----------------------------------------------------------
  // Overlays (open-overlay actions, e.g. size guide modals)
  // -----------------------------------------------------------
  function initOverlays() {
    if (window.__laliPdpOverlaysBound) return;
    window.__laliPdpOverlaysBound = true;

    // Inject overlay open/close CSS. The original instant-poUGqa1UxnhvNmP8.css has
    // unconditional `display:flex` rules per overlay id that ignore data-state,
    // so we use !important to actually hide/show on toggle.
    if (!document.getElementById('lali-pdp-overlay-base')) {
      var s = document.createElement('style');
      s.id = 'lali-pdp-overlay-base';
      s.textContent =
        '[data-instant-type="overlay"]{z-index:99998}' +
        '[data-instant-type="overlay"][data-state="closed"]{display:none!important;pointer-events:none!important;visibility:hidden!important}' +
        '[data-instant-type="overlay"][data-state="open"]{display:flex!important;pointer-events:auto!important;visibility:visible!important}';
      document.head.appendChild(s);
    }

    // Open / close on data-instant-action-type="open-overlay" buttons.
    // Use capture phase so we run before any other listener that could
    // stopPropagation. If action-id is present → open that overlay. If not →
    // close the enclosing overlay (this is the X close button pattern).
    // Also handle "switch" buttons inside one overlay that target another:
    // we close the current overlay before opening the new one.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-instant-action-type="open-overlay"]');
      if (!btn) return;
      var targetId = btn.getAttribute('data-instant-action-id');
      var enclosingOverlay = btn.closest('[data-instant-type="overlay"]');

      if (!targetId || targetId === '') {
        // X close button (no target) → close enclosing overlay.
        if (enclosingOverlay) {
          e.preventDefault();
          e.stopPropagation();
          closeOverlay(enclosingOverlay);
        }
        return;
      }

      // Open target overlay. If the button is inside another overlay, close
      // that one first so we don't stack them.
      var overlay = document.getElementById('i' + targetId) || document.getElementById(targetId);
      if (!overlay) return;
      e.preventDefault();
      e.stopPropagation();
      if (enclosingOverlay && enclosingOverlay !== overlay) {
        closeOverlay(enclosingOverlay);
      }
      openOverlay(overlay);
    }, true);

    // Click on the backdrop (the overlay element itself, not its content) → close.
    document.addEventListener('click', function (e) {
      var open = e.target.closest && e.target.matches && e.target.matches('[data-instant-type="overlay"][data-state="open"]');
      if (open) {
        closeOverlay(e.target);
      }
    }, true);

    // Close on Esc
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('[data-instant-type="overlay"][data-state="open"]');
      if (open) closeOverlay(open);
    });
  }

  function openOverlay(el) {
    el.setAttribute('data-state', 'open');
    document.body.style.overflow = 'hidden';
  }

  function closeOverlay(el) {
    el.setAttribute('data-state', 'closed');
    // Only release scroll if NO other overlay is still open.
    if (!document.querySelector('[data-instant-type="overlay"][data-state="open"]')) {
      document.body.style.overflow = '';
    }
  }

  // -----------------------------------------------------------
  // Boot
  // -----------------------------------------------------------
  function boot() {
    whenSwiperReady(initSliders);
    initAccordions();
    initTickers();
    initCountdowns();
    initOverlays();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init if new sliders/accordions get injected later (e.g., by app blocks).
  var mo = new MutationObserver(function () {
    initAccordions();
    initTickers();
    initCountdowns();
    if (typeof window.Swiper !== 'undefined') initSliders();
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
